import type { BusinessCalendar } from '../business-calendars/platform-business-calendars.models';
import { isEqual } from 'lodash-es';
import {
  requireWorkflowPayloadVersion,
  type WorkflowDefinitionDocument,
  type WorkflowStepTimer,
  type WorkflowTimerAction,
} from './platform-workflow-definition-lifecycle.models';
import { automatedLiteralError } from './workflow-automated-step-authoring';
import {
  automationOperation,
  compatibleAutomationContexts,
  type AutomationOperation,
  type WorkflowAutomationCatalog,
} from './workflow-automation-authoring';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';
import { initializeBlankStep } from './workflow-step-composition';

export const timerMaximumMinutes = 5_270_400;
export const timerMaximumDays = 3660;
const code = (value: string) =>
  typeof value === 'string' &&
  value.length <= 120 &&
  /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(value);
const timerFields = [
  'version',
  'id',
  'label',
  'startCode',
  'durationUnit',
  'durationAmount',
  'calendar',
  'dueLocalTime',
  'gapCode',
  'overlapCode',
  'pauseCode',
  'resetCode',
  'reentryCode',
  'warningLeadMinutes',
  'warningAction',
  'breachAction',
];
const actionFields = [
  'id',
  'typeCode',
  'command',
  'inputs',
  'outcomes',
  'group',
  'order',
  'mode',
  'failurePolicy',
  'caller',
  'requiredForProgression',
];

export function timerOperationEditable(
  operation: AutomationOperation,
): boolean {
  return (
    operation.availableForNewUse &&
    operation.packPermitted &&
    operation.caller === 'scoped-system' &&
    operation.modes.includes('durable') &&
    operation.input.fields.every((field) =>
      ['string', 'integer', 'number', 'boolean'].includes(field.type),
    )
  );
}

/** Advisory installed-provider compatibility for this exact draft step; never an authorization grant. */
export function timerOperationPolicyError(
  operation: AutomationOperation,
  document: WorkflowDefinitionDocument | null,
  nodeId: string | null,
): string | null {
  const supported = operation.supportedAssignmentBases;
  if (supported === undefined) return null;
  const unavailable =
    "This operation needs this step's exact assignment policy. Review Work assignment before choosing it.";
  if (
    !Array.isArray(supported) ||
    supported.length === 0 ||
    new Set(supported).size !== supported.length ||
    supported.some((basis) => basis !== 'queues' && basis !== 'roles') ||
    !document ||
    !nodeId ||
    !Array.isArray(document.nodes) ||
    !Array.isArray(document.assignmentPolicies)
  )
    return unavailable;
  const object = (value: unknown): boolean =>
    !!value && typeof value === 'object' && !Array.isArray(value);
  if (
    document.nodes.some((node) => !object(node)) ||
    document.assignmentPolicies.some((policy) => !object(policy))
  )
    return unavailable;
  const nodes = document.nodes.filter((node) => node.id === nodeId);
  if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step')
    return unavailable;
  const policyRows = document.assignmentPolicies.filter(
    (policy): policy is NonNullable<typeof policy> => policy !== null,
  );
  const policies = policyRows.filter(
    (policy) => policy.scopeCode === 'step' && policy.nodeId === nodeId,
  );
  if (
    policies.length !== 1 ||
    typeof policies[0].id !== 'string' ||
    !policies[0].id.trim() ||
    policyRows.filter((policy) => policy.id === policies[0].id).length !== 1
  )
    return unavailable;
  if (
    nodes[0].declaration != null &&
    (!object(nodes[0].declaration) || !object(nodes[0].declaration?.behavior))
  )
    return unavailable;
  const selected = nodes[0].declaration?.behavior?.assignmentPolicyId;
  if (selected != null && selected !== policies[0].id) return unavailable;
  const rawBasis = policies[0].assignmentBasisCode;
  const basis =
    rawBasis == null ? 'queues' : rawBasis === 'roles' ? 'roles' : null;
  if (basis === null) return unavailable;
  return supported.includes(basis)
    ? null
    : "This operation cannot run as a scheduled action with this step's assignment policy. Choose another operation or review Work assignment.";
}

export function timerFingerprint(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
): string {
  return JSON.stringify([
    document.workflowCode,
    document.packReference,
    document.capabilityReferences,
    document.nodes.map((node) => ({
      id: node.id,
      typeCode: node.typeCode,
      ...(node.id === nodeId ? { behavior: node.declaration?.behavior } : {}),
    })),
    nodeId,
  ]);
}

export function timerActionError(
  action: WorkflowTimerAction,
  catalog: WorkflowAutomationCatalog,
  document: WorkflowDefinitionDocument,
  nodeId: string | null = null,
): string | null {
  if (
    !action ||
    Object.keys(action).some((key) => !actionFields.includes(key)) ||
    !code(action.id) ||
    !code(action.group) ||
    action.typeCode !== 'command' ||
    action.mode !== 'durable' ||
    action.caller !== 'scoped-system' ||
    action.requiredForProgression !== false ||
    action.order !== 0 ||
    !catalog.failurePolicies.includes(action.failurePolicy)
  )
    return 'Choose an installed durable operation and its failure policy for both timer stages.';
  const operation = automationOperation(catalog, action);
  if (!operation || !timerOperationEditable(operation))
    return 'The exact installed timer operation is unavailable for editing. Its saved settings are preserved.';
  const policyError = timerOperationPolicyError(operation, document, nodeId);
  if (policyError) return policyError;
  if (
    !Array.isArray(action.outcomes) ||
    [...action.outcomes].sort().join('|') !==
      [...operation.outcomes].sort().join('|')
  )
    return 'The saved operation results no longer match its exact installed contract.';
  if (
    !Array.isArray(action.inputs) ||
    new Set(action.inputs.map((input) => input.field)).size !==
      action.inputs.length ||
    action.inputs.some(
      (input) =>
        Object.keys(input).some(
          (key) =>
            !['field', 'source', 'value', 'context', 'literalJson'].includes(
              key,
            ),
        ) ||
        !operation.automationInputs.includes(input.field) ||
        !operation.input.fields.some((field) => field.name === input.field),
    )
  )
    return 'Use each registered timer operation input at most once.';
  for (const field of operation.input.fields) {
    const input = action.inputs.find((binding) => binding.field === field.name);
    if (!input) {
      if (field.required) return `Supply ${field.name} for ${operation.label}.`;
      continue;
    }
    if (input.source === 'literal') {
      if (input.context != null)
        return 'Choose one value source for each operation input.';
      const error = automatedLiteralError(field, input);
      if (error) return `${field.name}: ${error}`;
    } else if (
      input.source !== 'context' ||
      input.value != null ||
      input.literalJson != null ||
      !compatibleAutomationContexts(
        catalog,
        'scheduled-item',
        field,
        document,
      ).some(
        (context) =>
          [
            'workflow.id',
            'occurrence.id',
            'subject.id',
            'workflow.etag',
            'initiatingActor.id',
          ].includes(context.code) && context.code === input.context,
      )
    )
      return `Choose a compatible captured process value for ${field.name}.`;
  }
  return null;
}

export function timerRuleError(
  timer: WorkflowStepTimer,
  catalog: WorkflowAutomationCatalog,
  calendars: readonly BusinessCalendar[],
  document: WorkflowDefinitionDocument,
  nodeId: string | null = null,
): string | null {
  if (
    !timer ||
    Object.keys(timer).some((field) => !timerFields.includes(field)) ||
    timer.version !== 1 ||
    !code(timer.id)
  )
    return 'This saved timer contract is unavailable. Its configuration is preserved for inspection.';
  if (
    typeof timer.label !== 'string' ||
    !timer.label.trim() ||
    timer.label !== timer.label.trim() ||
    timer.label.length > 160
  )
    return 'Enter a timer name of up to 160 characters.';
  if (
    timer.startCode !== 'on_entry' ||
    timer.reentryCode !== 'restart_new_occurrence' ||
    !['disabled', 'manual_before_warning'].includes(timer.pauseCode) ||
    !['disabled', 'restart_from_now'].includes(timer.resetCode)
  )
    return 'Choose when the timer starts, its pause/reset rules and fresh-visit behavior.';
  if (
    !['elapsed_minutes', 'calendar_days', 'working_days'].includes(
      timer.durationUnit,
    ) ||
    !Number.isInteger(timer.durationAmount) ||
    timer.durationAmount < 1 ||
    timer.durationAmount >
      (timer.durationUnit === 'elapsed_minutes'
        ? timerMaximumMinutes
        : timerMaximumDays)
  )
    return 'Choose a duration unit and a whole-number amount within the supported range.';
  if (
    !Number.isInteger(timer.warningLeadMinutes) ||
    timer.warningLeadMinutes < 1 ||
    timer.warningLeadMinutes > timerMaximumMinutes
  )
    return 'Enter the warning lead in whole minutes from 1 to 5,270,400.';
  if (timer.durationUnit === 'elapsed_minutes') {
    if (
      timer.calendar != null ||
      timer.dueLocalTime != null ||
      timer.gapCode != null ||
      timer.overlapCode != null
    )
      return 'Elapsed-minute timers cannot carry calendar or local-time settings.';
    if (timer.warningLeadMinutes > timer.durationAmount)
      return 'The warning must not fall before the timer starts.';
  } else {
    if (
      !timer.calendar ||
      Object.keys(timer.calendar).some(
        (key) => !['code', 'versionCode', 'contentHash'].includes(key),
      ) ||
      calendars.filter(
        (calendar) =>
          calendar.code === timer.calendar!.code &&
          calendar.versionCode === timer.calendar!.versionCode &&
          calendar.contentHash === timer.calendar!.contentHash,
      ).length !== 1
    )
      return 'Choose one available published calendar version.';
    if (
      !timer.dueLocalTime ||
      !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(timer.dueLocalTime) ||
      timer.gapCode !== 'reject' ||
      !['reject', 'earlier', 'later'].includes(timer.overlapCode ?? '')
    )
      return 'Choose the local due time and explicit daylight-saving rules.';
  }
  return (
    timerActionError(timer.warningAction, catalog, document, nodeId) ??
    timerActionError(timer.breachAction, catalog, document, nodeId)
  );
}

export function applyStepTimers(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  timers: readonly WorkflowStepTimer[],
  original: readonly WorkflowStepTimer[],
  catalog: WorkflowAutomationCatalog,
  calendars: readonly BusinessCalendar[],
): WorkflowDefinitionDocument {
  if (catalog.contractVersion !== 1)
    throw new Error('Reload installed timer operations before applying.');
  const nodes = document.nodes.filter(
    (node) => node.id === nodeId && node.typeCode === 'human_step',
  );
  if (
    nodes.length !== 1 ||
    timers.length > 16 ||
    new Set(timers.map((timer) => timer.id)).size !== timers.length
  )
    throw new Error(
      'Select one work step and at most 16 uniquely identified timers.',
    );
  const references = [...(document.capabilityReferences ?? [])];
  const actionIds = new Set<string>();
  for (const timer of timers) {
    const retained = original.find((item) => item.id === timer.id);
    const unchanged =
      retained && JSON.stringify(retained) === JSON.stringify(timer);
    if (!unchanged) {
      const error = timerRuleError(timer, catalog, calendars, document, nodeId);
      if (error) throw new Error(error);
    }
    for (const action of [timer.warningAction, timer.breachAction]) {
      if (!action) {
        if (unchanged) continue;
        throw new Error('Choose an operation for each timer stage.');
      }
      if (actionIds.has(action.id))
        throw new Error('Each timer stage needs a distinct action identity.');
      actionIds.add(action.id);
      if (unchanged) continue;
      const operation = automationOperation(catalog, action)!;
      for (const reference of [
        operation.command,
        ...operation.requiredCapabilities,
      ]) {
        const existing = references.filter(
          (pin) =>
            capabilityIdentity(pin.key) === capabilityIdentity(reference.key),
        );
        if (
          existing.length > 1 ||
          existing.some((pin) => !sameCapabilityReference(pin, reference))
        )
          throw new Error(
            'A saved operation pin has a different exact contract. Repair it before applying the timer.',
          );
        if (!existing.length) references.push(structuredClone(reference));
      }
    }
  }
  if (references.length > 100)
    throw new Error('A workflow supports at most 100 capability references.');
  const saved = nodes[0].declaration?.behavior?.timers;
  if (saved != null && !Array.isArray(saved))
    throw new Error(
      'The saved timer container is unavailable for editing. Its configuration is preserved.',
    );
  if (isEqual(timers, saved ?? [])) return document;
  const next = initializeBlankStep(document, nodeId);
  return {
    ...next,
    payloadSchemaVersion: timers.length
      ? requireWorkflowPayloadVersion(next.payloadSchemaVersion, '1.16')
      : next.payloadSchemaVersion,
    capabilityReferences: references,
    nodes: next.nodes.map((node) =>
      node.id !== nodeId
        ? node
        : {
            ...node,
            declaration: {
              ...node.declaration!,
              behavior: {
                ...node.declaration!.behavior,
                timers: structuredClone([...timers]),
              },
            },
          },
    ),
  };
}
